import {
    Badge,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Icon,
    useIsMobile,
} from "@elit-hair/elit-ui";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle, TbBell, TbMicrophone } from "react-icons/tb";

import { PermissionArrow } from "@/components/permissions-modal/permissions-arrow";
import { cn } from "@/lib/utils.ts";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions.tsx";

type PermissionModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const PermissionStateButton = ({
    granted,
    onRequest,
    icon,
    title,
    description,
}: {
    granted: boolean;
    onRequest: () => void;
    icon: React.ReactNode;
    title: string;
    description: string;
}) => {
    const { t } = useTranslation();

    return (
        <button
            type="button"
            disabled={granted}
            className={cn(
                "group w-full flex items-center justify-between rounded-lg border bg-background text-foreground px-3 py-3 shadow-sm transition-colors text-left",
                !granted && "hover:bg-accent hover:text-accent-foreground cursor-pointer",
                granted && "opacity-60 cursor-default"
            )}
            onClick={onRequest}
        >
            <div className="flex items-center gap-x-3 text-left min-w-0 flex-1 overflow-hidden">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground ring-1 ring-inset ring-border/40 shrink-0">
                    {icon}
                </div>
                <div className="space-y-0.5 min-w-0 flex-1 overflow-hidden">
                    <h4 className="text-sm font-medium leading-none truncate">{title}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 wrap-break-words">{description}</p>
                </div>
            </div>
            {granted && (
                <div className="flex items-center gap-2 shrink-0 ml-2">
                    <Badge label={t("granted")} variant="success" className="pl-2 pr-2" />
                </div>
            )}
        </button>
    );
};

export const PermissionModal: React.FC<PermissionModalProps> = ({ isOpen, onClose }) => {
    const { permissions, requestPermission } = usePermissions();
    const [showArrow, setShowArrow] = useState(false);
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    useEffect(() => {
        setShowArrow(isOpen);
    }, [isOpen]);

    return (
        <>
            {!isMobile && <PermissionArrow isVisible={showArrow} />}
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-[90vw] sm:max-w-xl">
                    <DialogHeader className="flex flex-col gap-2 wrap-break-words">
                        <DialogTitle>{t("permissions_required")}</DialogTitle>
                        <DialogDescription className="wrap-break-words">
                            {t("permissions_required_description")}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <PermissionStateButton
                            granted={permissions[PermissionNames.MICROPHONE] === "granted"}
                            onRequest={() => requestPermission(PermissionNames.MICROPHONE)}
                            icon={<Icon icon={TbMicrophone} />}
                            title={t("microphone")}
                            description={t("microphone_description")}
                        />
                        <PermissionStateButton
                            granted={permissions[PermissionNames.NOTIFICATIONS] === "granted"}
                            onRequest={() => requestPermission(PermissionNames.NOTIFICATIONS)}
                            icon={<Icon icon={TbBell} />}
                            title={t("notifications")}
                            description={t("notifications_description")}
                        />
                        <div className="flex gap-2">
                            <Icon icon={TbAlertCircle} className="text-amber-500/70 min-w-4 min-h-4" />
                            <p className="text-xs text-amber-500/70 wrap-break-words">{t("google_chrome_changes")}</p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
